import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Call, Device } from "@twilio/voice-sdk";
import { api } from "../api";
import { Icon } from "../lib/Icon";

/**
 * Docked softphone (§ softphone) — present on every screen when the `telephony`
 * flag is on. Places and receives real calls via the Twilio Voice SDK, which is
 * lazy-imported only once a valid token arrives (so the WebRTC bundle never
 * loads when telephony is off/unconfigured). Text/Verify tabs are wired with SMS
 * in a later PR. All SDK interaction is guarded so a telephony error can never
 * take down the app Shell this lives in.
 */
type Tab = "dial" | "text" | "verify";
type CallState = "idle" | "outgoing" | "incoming" | "connected";
const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

export function Softphone() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>("dial");
  const [number, setNumber] = useState("");
  const [deviceReady, setDeviceReady] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [callState, setCallState] = useState<CallState>("idle");
  const [muted, setMuted] = useState(false);
  const [peer, setPeer] = useState(""); // the other party (number or identity)
  const [peerKind, setPeerKind] = useState(""); // known / integration / unknown (inbound)
  const [peerNumber, setPeerNumber] = useState(""); // the other party's number (for transfer context)
  const [smsTo, setSmsTo] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const sms = useMutation({
    mutationFn: () => api.sendSms(smsTo.trim(), smsBody.trim()),
    onSuccess: (r) => { if (r.ok) setSmsBody(""); },
  });
  const [showXfer, setShowXfer] = useState(false);
  const xferAgents = useQuery({ queryKey: ["xfer-agents"], queryFn: api.transferAgents, enabled: showXfer });
  const xfer = useMutation({
    mutationFn: (toId: string) =>
      api.transferCall(callRef.current?.parameters.CallSid ?? "", toId, { name: peer, kind: peerKind || "unknown", number: peerNumber }),
    onSuccess: (r) => { if (r.ok) setShowXfer(false); },
  });

  // Long staleTime + no reconnect/focus refetch: the token lives an hour and the
  // Device refreshes it IN PLACE via tokenWillExpire. If this query refetched and
  // returned a new JWT string mid-call, the Device effect below would rebuild and
  // drop the live call on a transient network blip — so we don't let it.
  const token = useQuery({
    queryKey: ["telephony-token"],
    queryFn: api.telephonyToken,
    // `retry` applies only to a thrown fetch (a transient 5xx/429/network blip, or
    // an Access-redirect non-JSON body) — never to a settled configured:false
    // result — so a bounded retry recovers the phone from a blip without risking a
    // live call (there's no token yet to rebuild the Device around).
    retry: 2,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 55 * 60 * 1000,
  });
  // Live count of callers on hold, so an agent can pick up the next one. Only
  // polls when the phone is ready and idle; fails soft to 0.
  const queueQ = useQuery({
    queryKey: ["telephony-queue"],
    queryFn: api.queueWaiting,
    enabled: !!token.data?.configured && deviceReady && callState === "idle",
    refetchInterval: 20000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const waiting = queueQ.data?.waiting ?? 0;
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // Current token kept in a ref so the Device is built ONCE (keyed on presence,
  // not value) and refreshed in place — never torn down when the value changes.
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = token.data?.token;
  const hasToken = !!token.data?.token;

  // Bind lifecycle events for a call (outgoing or incoming) to our render state.
  function bindCall(call: Call) {
    callRef.current = call;
    setMuted(false);
    call.on("accept", () => setCallState("connected"));
    const clear = () => { callRef.current = null; setCallState("idle"); setPeer(""); setPeerKind(""); setPeerNumber(""); setShowXfer(false); };
    call.on("disconnect", clear);
    call.on("cancel", clear);
    call.on("reject", clear);
    call.on("error", clear);
  }

  // Spin up the Device ONCE when a token first exists; tear it down only on
  // unmount or token loss (keyed on `hasToken`, not the token value).
  useEffect(() => {
    const tok = tokenRef.current;
    if (!tok) return;
    let cancelled = false;
    let device: Device | null = null;
    // Safety net: if the Device never reaches "registered" (a stalled registration
    // or WebRTC handshake — usually a Twilio TwiML-app / API-key / network issue),
    // surface a clear error instead of leaving the phone silently on "Starting…".
    const readyTimer = setTimeout(() => {
      if (!cancelled) setDeviceError((prev) => prev ?? "Phone didn't finish connecting — check the Twilio setup or your network, then reload.");
    }, 12000);

    (async () => {
      try {
        const { Device } = await import("@twilio/voice-sdk");
        if (cancelled) return;
        device = new Device(tok, { closeProtection: true, logLevel: "error" });
        deviceRef.current = device;
        device.on("registered", () => {
          if (cancelled) return;
          clearTimeout(readyTimer);
          setDeviceError(null); // reaching "registered" clears any connecting-phase error
          setDeviceReady(true);
        });
        device.on("unregistered", () => !cancelled && setDeviceReady(false));
        device.on("error", (e: { message?: string }) => !cancelled && setDeviceError(e?.message ?? "Device error"));
        device.on("incoming", (call: Call) => {
          if (cancelled) return;
          // The inbound webhook passes the resolved caller as custom parameters.
          const cp = call.customParameters;
          setPeer(cp?.get("callerName") || call.parameters.From || "Unknown");
          setPeerKind(cp?.get("callerKind") || "");
          setPeerNumber(cp?.get("callerNumber") || call.parameters.From || "");
          setCallState("incoming");
          bindCall(call);
        });
        // Refresh the short-lived token before it expires.
        device.on("tokenWillExpire", async () => {
          try {
            const next = await api.telephonyToken();
            if (!cancelled && next.token) device!.updateToken(next.token);
          } catch { /* a failed refresh just lets the current token lapse */ }
        });
        await device.register();
      } catch (e) {
        if (!cancelled) setDeviceError(e instanceof Error ? e.message : "Couldn't start the phone");
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(readyTimer);
      try { callRef.current?.disconnect(); } catch { /* ignore */ }
      try { device?.destroy(); } catch { /* ignore */ }
      deviceRef.current = null;
      callRef.current = null;
      setDeviceReady(false);
      setCallState("idle");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasToken]);

  // The header's Call/Text buttons (Shell) open the softphone on a given tab via a
  // decoupled window event, so this component keeps owning its own open/tab state
  // (and its "a telephony error can't take down the Shell" isolation) — no props or
  // shared store threaded through the app.
  useEffect(() => {
    function onOpen(e: Event) {
      const t = (e as CustomEvent<{ tab?: Tab }>).detail?.tab;
      setOpen(true);
      if (t === "dial" || t === "text" || t === "verify") setTab(t);
    }
    window.addEventListener("softphone:open", onOpen);
    return () => window.removeEventListener("softphone:open", onOpen);
  }, []);

  const inCall = callState !== "idle";
  const configured = !!token.data?.configured;

  async function dial() {
    const dev = deviceRef.current;
    if (!dev || !deviceReady || !number.trim() || inCall) return;
    try {
      const call = await dev.connect({ params: { To: number.trim() } });
      setPeer(number.trim());
      setPeerNumber(number.trim());
      setPeerKind("");
      setCallState("outgoing");
      bindCall(call);
    } catch (e) {
      setDeviceError(e instanceof Error ? e.message : "Call failed");
    }
  }
  async function pickupQueue() {
    const dev = deviceRef.current;
    if (!dev || !deviceReady || inCall) return;
    try {
      // To="queue" is the sentinel the voice webhook turns into <Dial><Queue>,
      // bridging this agent to the longest-waiting caller.
      const call = await dev.connect({ params: { To: "queue" } });
      setPeer("Waiting caller");
      setPeerNumber("");
      setPeerKind("");
      setCallState("outgoing");
      bindCall(call);
    } catch (e) {
      setDeviceError(e instanceof Error ? e.message : "Couldn't pick up");
    }
  }
  function hangup() { try { callRef.current?.disconnect(); } catch { /* ignore */ } }
  function acceptIncoming() { try { callRef.current?.accept(); } catch { /* ignore */ } }
  function rejectIncoming() { try { callRef.current?.reject(); } catch { /* ignore */ } }
  function toggleMute() {
    const call = callRef.current;
    if (!call) return;
    const next = !muted;
    try { call.mute(next); setMuted(next); } catch { /* ignore */ }
  }

  const reason = token.data?.reason;
  const statusLabel = token.isLoading
    ? "Connecting…"
    : token.isError
      ? "Can't reach phone"
      : reason === "forbidden"
        ? "No phone access"
        : reason === "telephony_disabled"
          ? "Telephony off"
          : !configured
            ? "Not set up yet"
            : deviceError
              ? "Phone error"
              : deviceReady
                ? "Ready"
                : "Starting…";
  const statusColor = deviceReady && configured && !deviceError ? "#1f8a5b" : configured && !deviceError ? "#9a7b1f" : "#8a8e88";

  return (
    <div style={{ position: "fixed", right: 18, bottom: 18, zIndex: 40, fontFamily: "inherit" }}>
      {open ? (
        <div style={{ width: 300, background: "#fff", borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,.18), inset 0 0 0 1px rgba(0,0,0,.06)", overflow: "hidden" }}>
          <div style={{ background: "#006747", color: "#fff", padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="phone" size={15} color="#fff" />
            <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>Softphone</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, opacity: 0.9 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor === "#8a8e88" ? "#cfd3cd" : statusColor }} />
              {statusLabel}
            </span>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ marginLeft: 4, border: 0, background: "rgba(255,255,255,.15)", color: "#fff", width: 22, height: 22, borderRadius: 6, cursor: "pointer", fontSize: 14, lineHeight: 1 }}>×</button>
          </div>

          {!configured && !token.isLoading && (
            <div style={{ background: "#fbf6e9", color: "#7a5f14", fontSize: 11.5, padding: "8px 14px", borderBottom: "1px solid #efe2c0" }}>
              {token.isError
                ? "Couldn't reach the phone service — try reloading."
                : reason === "forbidden"
                  ? "Your account doesn't have phone access yet."
                  : reason === "telephony_disabled"
                    ? "Telephony is currently turned off."
                    : reason && reason !== "telephony_not_configured"
                      ? "Couldn't reach the phone service — try reloading."
                      : "Telephony isn't connected yet — add the Twilio credentials to enable it."}
            </div>
          )}
          {deviceError && (
            <div style={{ background: "#fbe9e7", color: "#b3261e", fontSize: 11.5, padding: "8px 14px", borderBottom: "1px solid #f3d3ce" }}>{deviceError}</div>
          )}

          {inCall ? (
            <div style={{ padding: "20px 16px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#8a8e88" }}>
                {callState === "incoming" ? "Incoming call" : callState === "outgoing" ? "Calling…" : "On call"}
              </div>
              <div style={{ fontSize: 20, fontFamily: "'Space Mono',monospace", fontWeight: 600 }}>{peer || "—"}</div>
              {peerKind === "unknown" ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: "#b3261e", background: "#fbe4e2", borderRadius: 999, padding: "3px 10px" }}>
                  <Icon name="alert-triangle" size={13} color="#b3261e" /> Not in our system — verify identity
                </div>
              ) : peerKind === "integration" ? (
                <div style={{ fontSize: 11, fontWeight: 700, color: "#4a5675", background: "#e8ebf0", borderRadius: 999, padding: "3px 10px" }}>Integration</div>
              ) : null}
              {callState === "incoming" ? (
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={rejectIncoming} style={ROUND_BTN("#c0392b")}><Icon name="phone" size={20} color="#fff" /></button>
                  <button onClick={acceptIncoming} style={ROUND_BTN("#1f8a5b")}><Icon name="phone" size={20} color="#fff" /></button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={toggleMute} style={ROUND_BTN(muted ? "#9a7b1f" : "#6b706a")} title={muted ? "Unmute" : "Mute"}>
                      <Icon name={muted ? "mic-off" : "mic"} size={18} color="#fff" />
                    </button>
                    <button onClick={() => setShowXfer((v) => !v)} style={ROUND_BTN(showXfer ? "#006747" : "#6b706a")} title="Transfer">
                      <Icon name="git-branch" size={18} color="#fff" />
                    </button>
                    <button onClick={hangup} style={ROUND_BTN("#c0392b")} title="Hang up"><Icon name="phone-off" size={18} color="#fff" /></button>
                  </div>
                  {showXfer && (
                    <div style={{ width: "100%", background: "#faf9f6", border: "1px solid #eceae3", borderRadius: 8, padding: "8px 10px", maxHeight: 160, overflowY: "auto" }}>
                      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "#8a8e88", marginBottom: 6 }}>Transfer to…</div>
                      {xferAgents.isLoading ? (
                        <div style={{ fontSize: 12, color: "#8a8e88" }}>Loading…</div>
                      ) : (xferAgents.data ?? []).length === 0 ? (
                        <div style={{ fontSize: 12, color: "#8a8e88" }}>No other agents.</div>
                      ) : (
                        (xferAgents.data ?? []).map((a) => (
                          <button key={a.id} onClick={() => !xfer.isPending && xfer.mutate(a.id)} disabled={xfer.isPending} style={{ display: "block", width: "100%", textAlign: "left", fontSize: 12.5, padding: "7px 8px", border: 0, borderRadius: 6, background: "none", cursor: xfer.isPending ? "default" : "pointer", color: "#1d241f" }}>
                            {a.name}
                          </button>
                        ))
                      )}
                      {xfer.isError && <div style={{ fontSize: 11, color: "#b3261e", marginTop: 4 }}>Transfer failed — try again.</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", borderBottom: "1px solid #f0efe9" }}>
                {(["dial", "text", "verify"] as Tab[]).map((t) => (
                  <button key={t} onClick={() => setTab(t)} style={{ flex: 1, fontSize: 12, fontWeight: 600, padding: "9px 0", border: 0, borderBottom: `2px solid ${tab === t ? "#006747" : "transparent"}`, background: "none", color: tab === t ? "#006747" : "#8a8e88", cursor: "pointer", textTransform: "capitalize" }}>{t}</button>
                ))}
              </div>
              <div style={{ padding: 14 }}>
                {tab === "dial" && (
                  <>
                    {waiting > 0 && (
                      <button
                        onClick={pickupQueue}
                        disabled={!deviceReady}
                        style={{ width: "100%", boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 12.5, fontWeight: 700, color: "#fff", background: deviceReady ? "#1f8a5b" : "#b7c3bd", border: 0, borderRadius: 8, padding: "9px 0", marginBottom: 10, cursor: deviceReady ? "pointer" : "default" }}
                      >
                        <Icon name="bell-ring" size={14} color="#fff" /> Pick up next — {waiting} waiting
                      </button>
                    )}
                    <input value={number} onChange={(e) => setNumber(e.target.value.replace(/[^\d*#+]/g, ""))} placeholder="Enter a number" style={{ width: "100%", boxSizing: "border-box", textAlign: "center", fontSize: 20, fontFamily: "'Space Mono',monospace", letterSpacing: 1, padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 8, marginBottom: 10 }} />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
                      {DIGITS.map((d) => (
                        <button key={d} onClick={() => setNumber((n) => n + d)} style={{ fontSize: 18, fontWeight: 600, padding: "11px 0", border: "1px solid #eceae3", borderRadius: 10, background: "#faf9f6", cursor: "pointer", color: "#1d241f" }}>{d}</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                      <button onClick={() => setNumber((n) => n.slice(0, -1))} disabled={!number} style={{ flex: "none", fontSize: 12, fontWeight: 600, color: "#8a8e88", background: "#fff", border: "1px solid #e2e0d8", borderRadius: 8, padding: "10px 12px", cursor: number ? "pointer" : "default" }}>⌫</button>
                      <button
                        onClick={dial}
                        disabled={!deviceReady || !number.trim()}
                        title={!configured ? "Telephony isn't connected yet" : !deviceReady ? "Phone still starting" : "Call"}
                        style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "#fff", background: deviceReady && number.trim() ? "#006747" : "#b7c3bd", border: 0, borderRadius: 8, padding: "10px 0", cursor: deviceReady && number.trim() ? "pointer" : "default" }}
                      >
                        <Icon name="phone" size={15} color="#fff" /> Call
                      </button>
                    </div>
                  </>
                )}
                {tab === "text" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="To (number)" disabled={!configured} style={configured ? SMS_INPUT : SOFT_INPUT} />
                    <textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Message" rows={3} maxLength={1000} disabled={!configured} style={{ ...(configured ? SMS_INPUT : SOFT_INPUT), resize: "none", fontFamily: "inherit" }} />
                    {(() => {
                      const canSend = configured && !!smsTo.trim() && !!smsBody.trim() && !sms.isPending;
                      return (
                        <button
                          onClick={() => canSend && sms.mutate()}
                          disabled={!canSend}
                          style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: canSend ? "#006747" : "#b7c3bd", border: 0, borderRadius: 8, padding: "9px 0", cursor: canSend ? "pointer" : "default" }}
                        >
                          {sms.isPending ? "Sending…" : "Send text"}
                        </button>
                      );
                    })()}
                    {sms.isError && <div style={{ ...HINT, color: "#b3261e" }}>Couldn't send — {sms.error instanceof Error ? sms.error.message : "try again"}</div>}
                    {!configured && <div style={HINT}>Telephony isn't connected yet.</div>}
                  </div>
                )}
                {tab === "verify" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <input placeholder="Phone to verify" disabled style={SOFT_INPUT} />
                    <button disabled style={SOFT_DISABLED_BTN}>Send verification code</button>
                    <div style={HINT}>One-time-code verification is wired up with SMS.</div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <button onClick={() => setOpen(true)} aria-label={waiting > 0 ? `Open softphone — ${waiting} waiting` : "Open softphone"} style={{ width: 52, height: 52, borderRadius: "50%", background: "#006747", border: 0, boxShadow: "0 6px 20px rgba(0,0,0,.22)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
          <Icon name="phone" size={20} color="#fff" />
          <span style={{ position: "absolute", right: 2, bottom: 2, width: 12, height: 12, borderRadius: "50%", background: inCall ? "#1f8a5b" : statusColor === "#8a8e88" ? "#cfd3cd" : statusColor, border: "2px solid #006747" }} />
          {waiting > 0 && (
            <span style={{ position: "absolute", top: -3, right: -3, minWidth: 18, height: 18, padding: "0 4px", boxSizing: "border-box", borderRadius: 9, background: "#c0392b", color: "#fff", fontSize: 10.5, fontWeight: 700, lineHeight: "18px", textAlign: "center", border: "2px solid #006747" }}>{waiting}</span>
          )}
        </button>
      )}
    </div>
  );
}

function ROUND_BTN(bg: string): React.CSSProperties {
  return { width: 52, height: 52, borderRadius: "50%", background: bg, border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
}
const SOFT_INPUT: React.CSSProperties = { width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 8, background: "#f6f5f1", color: "#8a8e88" };
const SMS_INPUT: React.CSSProperties = { width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 8, background: "#fff", color: "#1d241f" };
const SOFT_DISABLED_BTN: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#b7c3bd", border: 0, borderRadius: 8, padding: "9px 0", cursor: "default" };
const HINT: React.CSSProperties = { fontSize: 11, color: "#8a8e88", textAlign: "center" };
